import { BigNumber } from "bignumber.js";
import nutsRouletteContract, { NUTS_ROULETTE_CONSTANTS } from "@/contracts/nutsRoulette";
import elrondHelper from "@/helpers/elrond";
import { TokenTransfer, Transaction } from "@multiversx/sdk-core/out";
import wegldSwapContract from "@/contracts/wegldSwap";
import gamesProxyContract from "@/contracts/gamesProxy";
import { REFERRAL_GETTERS } from "@/store/referral";
import BLOCKCHAIN from "@/constants/blockchain";
import elrondApiHelper, { NFT_POST } from "@/helpers/elrondApi";
import ELROND from "@/constants/elrond";

export const NUTS_ROULETTE_GETTERS = {
  REWARDS: "nutsRouletteRewards",
};

export const NUTS_ROULETTE_MUTATIONS = {
  REWARDS: "nutsRouletteRewards",
};

export const NUTS_ROULETTE_ACTIONS = {
  PLAY: "nutsRoulettePlay",
  GET_REWARDS: "nutsRouletteGetRewards",
};

export interface REWARD {
  jackpot: BigNumber;
  nextGenJackpot: BigNumber;
  superJackpot: BigNumber;
  superJackpotWegld: BigNumber;
  specialChance: number;
  nutsNft?: NFT_POST;
  nextGenNft?: NFT_POST;
  wegldPerBullet: bigint;
  extraSpecialNormal?: TokenTransfer | NFT_POST;
  extraSpecialNextGen?: TokenTransfer | NFT_POST;
  extraSpecialSuper?: TokenTransfer | NFT_POST;
}

interface INutsRouletteStore {
  state: () => {
    rewards: REWARD;
  };

  [index: string]: any;
}

export const nutsRouletteStore: INutsRouletteStore = {
  state: () => ({
    rewards: null,
  }),
  getters: {
    [NUTS_ROULETTE_GETTERS.REWARDS](state) {
      return state.rewards;
    },
  },
  mutations: {
    [NUTS_ROULETTE_MUTATIONS.REWARDS](state, rewards) {
      state.rewards = rewards;
    },
  },
  actions: {
    async [NUTS_ROULETTE_ACTIONS.PLAY](
      { getters },
      { accountElrond, nbOfBullets, wegldBalance, jackpotType, selectedNftNonce, goNutsEgldPerBullet }
    ) {
      try {
        console.log("Starting nutsRoulette.play action with params:", {
          nbOfBullets,
          jackpotType,
          selectedNftNonce: selectedNftNonce ? `0x${selectedNftNonce.toString(16)}` : null,
          goNutsEgldPerBullet: goNutsEgldPerBullet ? goNutsEgldPerBullet.toString() : null
        });
        
        const transactions: Transaction[] = [];
        const rewards: REWARD = getters[NUTS_ROULETTE_GETTERS.REWARDS];

        if (!rewards) {
          console.error("Cannot play without rewards data");
          return null;
        }

        // Calculate required NUTS amount
        const requiredNuts = new BigNumber(nbOfBullets * NUTS_ROULETTE_CONSTANTS.NUTS_PER_BULLET);
        console.log("Required NUTS:", requiredNuts.toString());
        
        // Calculate required WEGLD amount with discount for 6 bullets
        let requiredWegld = new BigNumber(String(BigInt(nbOfBullets) * rewards.wegldPerBullet));
        if (nbOfBullets === 6) {
          requiredWegld = requiredWegld.minus(String(NUTS_ROULETTE_CONSTANTS.WEGLD_PER_BULLET / BigInt(2)));
        }
        console.log("Base required WEGLD:", requiredWegld.toString());

        // Adjust for GoNuts mode if enabled
        if (goNutsEgldPerBullet) {
          console.log("GO NUTS mode enabled, price per EGLD:", goNutsEgldPerBullet.toString());
          // Apply discount to WEGLD amount for GO NUTS mode
          requiredWegld = requiredWegld.times(NUTS_ROULETTE_CONSTANTS.GO_NUTS_WEGLD_WITH_DISCOUNT);
          
          // Add NUTS price in WEGLD (with slippage)
          requiredWegld = requiredWegld
            .plus(
              goNutsEgldPerBullet
                .multipliedBy(1.01) // 1% slippage
                .multipliedBy(nbOfBullets)
            )
            .decimalPlaces(0);
          
          console.log("GO NUTS adjusted WEGLD required:", requiredWegld.toString());
        }

        // Check if we need to wrap EGLD to WEGLD
        if (requiredWegld.gt(wegldBalance)) {
          const toWrap = requiredWegld.minus(wegldBalance);
          console.log("Need to wrap EGLD to WEGLD:", toWrap.toString());

          // Enhanced address handling to get a proper bech32 string
          let addressBech32 = "";
          
          try {
            // Log the address object for debugging
            console.log("Account address type:", typeof accountElrond.address);
            console.log("Account address properties:", Object.keys(accountElrond.address));
            
            // More robust address handling
            if (typeof accountElrond.address === 'string') {
              // If it's already a string, use it directly
              addressBech32 = accountElrond.address;
              console.log("Using address as string:", addressBech32);
            } else if (accountElrond.address) {
              // Try multiple ways to get the bech32 address
              if (typeof accountElrond.address.bech32 === 'function') {
                addressBech32 = accountElrond.address.bech32();
                console.log("Got bech32 from function:", addressBech32);
              } else if (accountElrond.address.bech32 !== undefined) {
                addressBech32 = String(accountElrond.address.bech32);
                console.log("Got bech32 from property:", addressBech32);
              } else if (accountElrond.address.toString) {
                addressBech32 = accountElrond.address.toString();
                console.log("Got bech32 from toString():", addressBech32);
              } else if (accountElrond.address.valueOf) {
                addressBech32 = String(accountElrond.address.valueOf());
                console.log("Got bech32 from valueOf():", addressBech32);
              }
            }
            
            if (!addressBech32) {
              throw new Error("Could not extract bech32 address");
            }
          } catch (error) {
            console.error("Error extracting bech32 address:", error);
            console.error("Account address:", accountElrond.address);
            return null;
          }
          
          // Create WRAP transaction
          console.log("Creating WRAP transaction with address:", addressBech32);
          const wrapTx = wegldSwapContract.wrapEgld(addressBech32, toWrap.toString());
          console.log("Created WRAP transaction:", wrapTx.getHash().toString());
          transactions.push(wrapTx);
        }

        // Create NFT token transfer if jackpot type requires it
        let nft: TokenTransfer = null;
        if (jackpotType === 1 && selectedNftNonce) {
          console.log("Adding NUTS NFT to transaction, nonce:", selectedNftNonce);
          nft = TokenTransfer.nonFungible(BLOCKCHAIN.NFT_TOKEN_IDENTIFIER, selectedNftNonce);
        } else if (jackpotType === 2 && selectedNftNonce) {
          console.log("Adding NextGen NFT to transaction, nonce:", selectedNftNonce);
          nft = TokenTransfer.nonFungible(BLOCKCHAIN.NEXT_GEN_NFT_TOKEN_IDENTIFIER, selectedNftNonce);
        }

        // Create NUTS ROULETTE transaction
        const referer = getters[REFERRAL_GETTERS.REFERER];
        const gameTransaction = await gamesProxyContract.nutsRoulette(
          accountElrond,
          nbOfBullets,
          requiredNuts,
          requiredWegld,
          nft,
          referer,
          !!goNutsEgldPerBullet
        );
        
        console.log("Created NUTS ROULETTE transaction:", gameTransaction.getHash().toString());
        transactions.push(gameTransaction);

        // Send all transactions
        console.log("Sending", transactions.length, "transactions");
        console.log("Transactions:", transactions.map(tx => tx.getHash().toString()));
        
        try {
          const result = await elrondHelper.sendTransactions(transactions, accountElrond);
          console.log("Transaction sending result:", result);
          return result;
        } catch (sendError) {
          console.error("Error sending transactions:", sendError);
          return null;
        }
      } catch (error) {
        console.error("Error in nutsRoulette.play action:", error);
        return null;
      }
    },
    async [NUTS_ROULETTE_ACTIONS.GET_REWARDS]({ commit }) {
      let [
        nutsJackpot,
        specialChance,
        wegldJackpot,
        nutsNfts,
        nextGenNfts,
        extraSpecialNormalAll,
        extraSpecialNextGenAll,
        extraSpecialSuperAll,
      ] = await Promise.all([
        nutsRouletteContract.getActualNutsJackpot(),
        nutsRouletteContract.getSpecialChance(),
        nutsRouletteContract.getWegldJackpot(),
        nutsRouletteContract.getNutsNfts(),
        nutsRouletteContract.getNextGenNfts(),
        Promise.resolve([]),
        Promise.resolve([]),
        Promise.resolve([]),
        // nutsRouletteContract.getExtraSpecialJackpots(3), // TODO: Re-enable special jackpots, but probably only in microservice
        // nutsRouletteContract.getExtraSpecialJackpots(1),
        // nutsRouletteContract.getExtraSpecialJackpots(2),
      ]);

      specialChance = specialChance ? specialChance / 100 : 10;

      const normalNft = nutsNfts.length ? await elrondApiHelper.getNft(nutsNfts[0]) : null;
      const nextGenNft = nextGenNfts.length
        ? await elrondApiHelper.getNft(nextGenNfts[0], BLOCKCHAIN.NEXT_GEN_NFT_TOKEN_IDENTIFIER)
        : null;

      const jackpot = nutsJackpot[0];
      const wegldPerBullet = jackpot.lt(new BigNumber(NUTS_ROULETTE_CONSTANTS.NUTS_PER_BULLET * 6))
        ? NUTS_ROULETTE_CONSTANTS.WEGLD_PER_BULLET / 2n
        : NUTS_ROULETTE_CONSTANTS.WEGLD_PER_BULLET;

      let extraSpecialNormal: TokenTransfer | NFT_POST | undefined = extraSpecialNormalAll.pop();
      let extraSpecialNextGen: TokenTransfer | NFT_POST | undefined = extraSpecialNextGenAll.pop();
      let extraSpecialSuper: TokenTransfer | NFT_POST | undefined = extraSpecialSuperAll.pop();

      if (extraSpecialNormal && extraSpecialNormal.nonce !== 0) {
        extraSpecialNormal = await elrondApiHelper.getNft(
          extraSpecialNormal.nonce,
          (extraSpecialNormal as TokenTransfer).tokenIdentifier
        );
      }
      if (extraSpecialNextGen && extraSpecialNextGen.nonce !== 0) {
        extraSpecialNextGen = await elrondApiHelper.getNft(
          extraSpecialNextGen.nonce,
          (extraSpecialNextGen as TokenTransfer).tokenIdentifier
        );
      }
      if (extraSpecialSuper && extraSpecialSuper.nonce !== 0) {
        extraSpecialSuper = await elrondApiHelper.getNft(
          extraSpecialSuper.nonce,
          (extraSpecialSuper as TokenTransfer).tokenIdentifier
        );
      }

      const rewards: REWARD = {
        jackpot,
        nextGenJackpot: nutsJackpot[1],
        superJackpot: nutsJackpot[2],
        superJackpotWegld: wegldJackpot,
        specialChance: specialChance,
        nutsNft: normalNft,
        nextGenNft,
        wegldPerBullet,
        extraSpecialNormal,
        extraSpecialNextGen,
        extraSpecialSuper,
      };

      commit(NUTS_ROULETTE_MUTATIONS.REWARDS, rewards);
    },
  },
};
